import React from 'react';
import { Container, Typography, Box, Card, CardContent, CardActionArea, Grid } from '@mui/material';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';

const MODES = [
  {
    id: 'eka',
    title: 'Eka Svara',
    subtitle: 'One Note',
    description: 'Hold Sa — the root note — for 16 seconds. The simplest, deepest practice. Just one sustained note to quiet the mind.',
    icon: '●',
    gradient: 'linear-gradient(135deg, #1a0533 0%, #3b1060 50%, #6a2090 100%)',
    accent: '#c084fc',
    notes: [{ note: 'Sa', freq: 261.63 }],
  },
  {
    id: 'aroha',
    title: 'Āroha',
    subtitle: 'The Ascent',
    description: 'Rise through the full sargam — Sa Re Ga Ma Pa Dha Ni Sa — each note held for 16 seconds. A complete sonic journey.',
    icon: '◈',
    gradient: 'linear-gradient(135deg, #001a33 0%, #003d66 50%, #005f99 100%)',
    accent: '#67e8f9',
    notes: [
      { note: 'Sa',  freq: 261.63 },
      { note: 'Re',  freq: 293.66 },
      { note: 'Ga',  freq: 329.63 },
      { note: 'Ma',  freq: 349.23 },
      { note: 'Pa',  freq: 392.00 },
      { note: 'Dha', freq: 440.00 },
      { note: 'Ni',  freq: 493.88 },
      { note: 'Sā',  freq: 523.25 },
    ],
  },
  {
    id: 'sveccha',
    title: 'Svecchā',
    subtitle: 'Free Choice',
    description: 'Choose the notes that call to you today. Build your own sequence and let intuition guide the practice.',
    icon: '✦',
    gradient: 'linear-gradient(135deg, #1a1a00 0%, #3d3300 50%, #665200 100%)',
    accent: '#fcd34d',
    notes: null, // set during selection
  },
];

const ALL_NOTES = [
  { note: 'Sa',  freq: 261.63 },
  { note: 'Re',  freq: 293.66 },
  { note: 'Ga',  freq: 329.63 },
  { note: 'Ma',  freq: 349.23 },
  { note: 'Pa',  freq: 392.00 },
  { note: 'Dha', freq: 440.00 },
  { note: 'Ni',  freq: 493.88 },
  { note: 'Sā',  freq: 523.25 },
];

const NadaPage = ({ onStart }) => {
  const [svecchaSelected, setSvecchaSelected] = React.useState(['Sa', 'Pa', 'Sā']);

  const toggleNote = (noteName) => {
    setSvecchaSelected(prev =>
      prev.includes(noteName)
        ? prev.length > 1 ? prev.filter(n => n !== noteName) : prev
        : [...prev, noteName]
    );
  };

  const handleStart = (mode) => {
    let notes = mode.notes;
    if (mode.id === 'sveccha') {
      notes = ALL_NOTES.filter(n => svecchaSelected.includes(n.note));
    }
    onStart({ ...mode, notes });
  };

  return (
    <Box
      sx={{
        minHeight: '100%',
        background: 'radial-gradient(ellipse at 30% 20%, #1e0a3c 0%, #0a0a18 60%, #000008 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        pt: { xs: 4, md: 6 },
        pb: 6,
        px: 2,
      }}
    >
      {/* Header */}
      <Box textAlign="center" mb={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1.5 }}>
          <SelfImprovementIcon sx={{ fontSize: 36, color: '#c084fc' }} />
          <Typography
            variant="h3"
            sx={{
              fontWeight: 300,
              color: '#f3e8ff',
              letterSpacing: 6,
              textTransform: 'uppercase',
              fontSize: { xs: '1.8rem', md: '2.4rem' },
            }}
          >
            Nāda
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{ color: '#a78bca', letterSpacing: 3, textTransform: 'uppercase', fontSize: '0.75rem' }}
        >
          Sound Meditation
        </Typography>
        <Box sx={{ width: 60, height: 1, backgroundColor: '#7c3aed44', mx: 'auto', mt: 2 }} />
        <Typography
          variant="body2"
          sx={{ color: '#6b5a85', mt: 2, maxWidth: 420, mx: 'auto', lineHeight: 1.8 }}
        >
          Choose your practice. There are no scores, no levels — only the sound and the silence between.
        </Typography>
      </Box>

      {/* Mode cards */}
      <Container maxWidth="md">
        <Grid container spacing={3} justifyContent="center">
          {MODES.map((mode) => (
            <Grid size={{ xs: 12, sm: 10, md: 4 }} key={mode.id}>
              <Card
                sx={{
                  background: mode.gradient,
                  border: `1px solid ${mode.accent}22`,
                  borderRadius: 4,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    boxShadow: `0 20px 60px ${mode.accent}25`,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleStart(mode)}
                  sx={{ p: 0 }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography
                      sx={{ fontSize: '2rem', mb: 1.5, display: 'block', textAlign: 'center' }}
                    >
                      {mode.icon}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        color: mode.accent,
                        fontWeight: 600,
                        letterSpacing: 2,
                        textAlign: 'center',
                        mb: 0.5,
                      }}
                    >
                      {mode.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: `${mode.accent}99`,
                        letterSpacing: 3,
                        textTransform: 'uppercase',
                        display: 'block',
                        textAlign: 'center',
                        mb: 2.5,
                        fontSize: '0.65rem',
                      }}
                    >
                      {mode.subtitle}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: '#c4b5d8', lineHeight: 1.7, textAlign: 'center', fontSize: '0.82rem' }}
                    >
                      {mode.description}
                    </Typography>

                    {/* Sveccha note picker */}
                    {mode.id === 'sveccha' && (
                      <Box sx={{ mt: 3 }} onClick={e => e.stopPropagation()}>
                        <Typography
                          variant="caption"
                          sx={{ color: '#a78bca', display: 'block', textAlign: 'center', mb: 1.5, letterSpacing: 2, textTransform: 'uppercase', fontSize: '0.6rem' }}
                        >
                          Select Notes
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2, justifyContent: 'center' }}>
                          {ALL_NOTES.map(n => {
                            const active = svecchaSelected.includes(n.note);
                            return (
                              <Box
                                key={n.note}
                                onClick={() => toggleNote(n.note)}
                                sx={{
                                  px: 2.5,
                                  py: 1.2,
                                  minWidth: 48,
                                  borderRadius: 2,
                                  border: `1px solid ${active ? '#fcd34d' : '#fcd34d44'}`,
                                  backgroundColor: active ? '#fcd34d28' : 'transparent',
                                  color: active ? '#fcd34d' : '#9878b8',
                                  fontSize: '0.9rem',
                                  fontWeight: active ? 700 : 400,
                                  letterSpacing: 1,
                                  textAlign: 'center',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  userSelect: 'none',
                                }}
                              >
                                {n.note}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                    <Box
                      sx={{
                        mt: 3,
                        py: 1,
                        borderRadius: 2,
                        background: `${mode.accent}18`,
                        border: `1px solid ${mode.accent}33`,
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        variant="button"
                        sx={{ color: mode.accent, letterSpacing: 3, fontSize: '0.72rem' }}
                      >
                        Begin →
                      </Typography>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default NadaPage;
